<template>
  <!-- モーダルとドロワーはどちらも window で Esc を受けて閉じるため、カレンダーが開いている間はここで止める -->
  <div @keydown.esc="closeOnEscape">
    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <button type="button" :aria-label="`Preferred dates: ${fieldText}`" :class="fieldStyle({ isOpen, isError: props.isError })">
          <span class="truncate" :class="hasValue ? 'text-black1' : 'text-gray2'">{{ fieldText }}</span>
          <img alt="" class="h-5 w-5 shrink-0" src="@/assets/icon/calendar/default.svg" />
        </button>
      </PopoverTrigger>
      <!-- 下向きは一般的な PC の高さで収まらず、画面ごとに向きが変わる。上向きに揃え、上にも収まらないときだけ下に開く -->
      <PopoverContent
        class="max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] overflow-y-auto rounded-none border-light-bd-muted bg-white p-3 font-Spectral shadow-lg"
        side="top"
        :aria-describedby="hintId"
        :collision-padding="8"
        :side-offset="4"
        data-vaul-no-drag
        disable-portal
      >
        <div class="mb-2 flex items-center justify-between gap-x-3 text-sm">
          <p :id="hintId" ref="hintRef" class="text-black2 outline-none" role="status" tabindex="-1">{{ hint }}</p>
          <button v-if="hasValue" class="text-gold3 underline hover:text-gold1" type="button" @click="clear">Clear</button>
        </div>
        <MoleculesWabunkaCalendar class="!bg-white" lang="en" :day="displayMonth">
          <template #content="{ day, isCurrentMonth }">
            <PreferredDateRangePickerDay :day="day" :from="props.from" :is-current-month="isCurrentMonth" :min-date="props.minDate" :on-click="onClickDay" :to="props.to" />
          </template>
        </MoleculesWabunkaCalendar>
      </PopoverContent>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { tv } from 'tailwind-variants';
import { useId } from 'vue';

import Popover from '@/components/lib/shadcn-vue/popover/Popover.vue';
import PopoverContent from '@/components/lib/shadcn-vue/popover/PopoverContent.vue';
import PopoverTrigger from '@/components/lib/shadcn-vue/popover/PopoverTrigger.vue';
import MoleculesWabunkaCalendar from '@/components/molecules/wabunka/Calendar/Calendar.vue';
import { getCurrentJstDayjs } from '@/utils/date';
import PreferredDateRangePickerDay from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/WaitlistRegistrationForm/PreferredDateRangePicker/PreferredDateRangePickerDay.vue';

interface Props {
  from: string;
  to: string;
  minDate: string;
  isError?: boolean;
  onChange: (from: string, to: string) => void;
}

const props = withDefaults(defineProps<Props>(), {
  isError: false,
});

const isOpen = ref(false);
const hintRef = ref<HTMLParagraphElement | null>(null);
const hintId = useId();

const hasValue = computed(() => props.from !== '');

// 文字列の日付を UTC モードで読む。ローカル TZ で読むと実行環境によって日付がずれる
const formatDate = (date: string) => dayjs.utc(date).locale('en').format('MMM D, YYYY');

const fieldText = computed(() => {
  if (!props.from) return 'Any dates';
  if (!props.to) return `From ${formatDate(props.from)}`;
  if (props.from === props.to) return formatDate(props.from);
  return `${formatDate(props.from)} — ${formatDate(props.to)}`;
});

const isSelectingEnd = computed(() => props.from !== '' && props.to === '');
const hint = computed(() => (isSelectingEnd.value ? 'Select an end date' : 'Select a start date'));

// 開いている間は from に追従させない。追従すると Clear で from が空になった瞬間に今月へ戻り、送った月を失う
const displayMonth = ref(getCurrentJstDayjs());
watch(isOpen, (open) => {
  if (open) displayMonth.value = props.from ? dayjs.utc(props.from) : getCurrentJstDayjs();
});

const onClickDay = (date: string) => {
  if (!isSelectingEnd.value || date < props.from) {
    props.onChange(date, '');
    return;
  }
  props.onChange(props.from, date);
  isOpen.value = false;
};

const clear = () => {
  props.onChange('', '');
  // Clear は押したボタンごと消える。欄へ戻すとカレンダーの外へ出た扱いで閉じるため、カレンダー内の案内へ移す
  hintRef.value?.focus();
};

const closeOnEscape = (event: KeyboardEvent) => {
  if (!isOpen.value) return;
  event.stopPropagation();
  isOpen.value = false;
};

const fieldStyle = tv({
  base: 'box-border flex h-12 w-full items-center justify-between gap-x-3 rounded-none border border-solid border-light-bd-muted bg-white px-4 text-left text-sm outline-none transition-all duration-100 ease-in-out',
  variants: {
    isOpen: {
      true: 'border-light-bd-accent',
    },
    isError: {
      true: 'border-red1 bg-red2',
    },
  },
});
</script>
